// SpacePage.tsx — Co Marca Admin — estilo CategoryPage
import Loading from '../../../components/Loading';
import React, { useEffect, useState, useCallback } from 'react';
import { Edit, Trash2, Plus, History, Store, ChevronRight, Home, Users, X } from 'lucide-react';
import { fetchEspacios, Espacio } from '../../../services/espacioService';
import AddEspacioModal from '../../../components/Spaces/AddEspacioModal';
import EditEspacioModal from '../../../components/Spaces/EditEspacioModal';
import DeleteEspacioModal from '../../../components/Spaces/DeleteEspacioModal';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useModelPermission } from '../../../hooks/useModelPermission';

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
.sp-page { font-family:'DM Sans',sans-serif; }
@keyframes spFadeUp { from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);} }
.sp-fadeup   { animation:spFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.sp-fadeup-1 { animation:spFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }
.sp-btn-primary {
    display:inline-flex;align-items:center;gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff;border:none;border-radius:11px;
    padding:9px 20px;font-size:.83rem;font-weight:800;
    cursor:pointer;font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 14px rgba(193, 52, 139,.25);
    transition:all .22s cubic-bezier(.16,1,.3,1);white-space:nowrap;
}
.sp-btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 22px rgba(193, 52, 139,.32);}
.sp-btn-secondary {
    display:inline-flex;align-items:center;gap:7px;
    background:#fff;border:1.5px solid #e2e8f0;
    color:#374151;border-radius:11px;
    padding:9px 14px;font-size:.83rem;font-weight:700;
    cursor:pointer;font-family:'DM Sans',sans-serif;transition:all .2s;
}
.sp-btn-secondary:hover{background:#f8fafc;border-color:${colors.secondary}40;color:${colors.secondary};}
.sp-table-row{transition:background .15s;}
.sp-table-row:hover{background:#fafcff;}
.sp-action-btn{width:30px;height:30px;border-radius:9px;border:none;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;}
.sp-img{width:44px;height:44px;border-radius:10px;object-fit:cover;cursor:pointer;border:1.5px solid #f1f5f9;transition:transform .2s;}
.sp-img:hover{transform:scale(1.08);}
.custom-scroll::-webkit-scrollbar{height:6px;width:6px;}
.custom-scroll::-webkit-scrollbar-track{background:transparent;}
.custom-scroll::-webkit-scrollbar-thumb{background-color:rgba(97, 37, 123,0.3);border-radius:20px;}
.custom-scroll::-webkit-scrollbar-thumb:hover{background-color:rgba(97, 37, 123,0.5);}
`;

const SpacePage: React.FC = () => {
    const [espacios, setEspacios]           = useState<Espacio[]>([]);
    const [showModal, setShowModal]         = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedEspacio, setSelectedEspacio] = useState<Espacio|null>(null);
    const [currentPage, setCurrentPage]     = useState(1);
    const itemsPerPage = 10;
    const navigate = useNavigate();
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('espacio', userLevelId);
    const [previewImg, setPreviewImg]       = useState<string|null>(null);

    const loadEspacios = useCallback(async () => {
        try {
            if (perm?.can_read) {
                const data = await fetchEspacios();
                setEspacios(data.sort((a,b)=>a.id-b.id));
            } else setEspacios([]);
        } catch { setEspacios([]); }
    }, [perm]);

    useEffect(() => { loadEspacios(); }, [loadEspacios]);

    const totalPages       = Math.ceil(espacios.length / itemsPerPage);
    const displayedEspacios = espacios.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);

    if (perm === null) return <Loading text="Cargando espacios..." />;

    return (
        <>
            <style>{css}</style>
            <div className="sp-page" style={{ background:'#fff', minHeight:'100vh', padding:'clamp(24px,3vw,36px) clamp(20px,4vw,40px)' }}>

                {/* HEADER */}
                <div className="sp-fadeup" style={{ marginBottom:'28px' }}>
                    <nav style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.72rem', fontWeight:'600', color:'#94a3b8', marginBottom:'12px' }}>
                        <a href="/dashboard" style={{ color:'#94a3b8', textDecoration:'none', display:'flex', alignItems:'center', gap:'3px', transition:'color .2s' }}
                            onMouseEnter={e=>e.currentTarget.style.color=colors.primary}
                            onMouseLeave={e=>e.currentTarget.style.color='#94a3b8'}>
                            <Home size={11}/> Dashboard
                        </a>
                        <ChevronRight size={10} color="#e2e8f0"/>
                        <span style={{ color:colors.primary, fontWeight:'700' }}>Espacios</span>
                    </nav>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'16px' }}>
                        <div>
                            <h1 style={{ fontSize:'clamp(1.5rem,3vw,2rem)', fontWeight:'900', color:'#0f172a', letterSpacing:'-.025em', lineHeight:1.1, marginBottom:'6px' }}>
                                Gestión de <span style={{ color:colors.primary }}>Espacios</span>
                            </h1>
                            <p style={{ fontSize:'.85rem', color:'#64748b', fontWeight:'500' }}>Administra los espacios físicos y su disponibilidad</p>
                            <div style={{ display:'flex', gap:'5px', marginTop:'10px' }}>
                                {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                    <div key={i} style={{ height:'3px', width:i===0?'24px':'8px', borderRadius:'3px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <div style={{ display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap' }}>
                            <button className="sp-btn-secondary" onClick={()=>navigate('/spaces/history')} title="Historial">
                                <History size={15} color={colors.secondary}/>
                            </button>
                            {perm.can_create && (
                                <button className="sp-btn-primary" onClick={()=>setShowModal(true)}>
                                    <Plus size={15}/> Agregar Espacio
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* TABLA */}
                <div className="sp-fadeup-1" style={{ background:'#fff', borderRadius:'20px', border:'1.5px solid #f1f5f9', boxShadow:'0 4px 24px rgba(0,0,0,.04)', overflow:'hidden' }}>

                    <ExpiredSessionModal isOpen={isSessionExpired} onClose={async()=>{ setIsSessionExpired(false); await logout(); }}/>
                    <AddEspacioModal isOpen={showModal} onClose={()=>setShowModal(false)} onEspacioAdded={loadEspacios}/>
                    <EditEspacioModal isOpen={showEditModal} onClose={()=>setShowEditModal(false)} onEspacioUpdated={loadEspacios} espacio={selectedEspacio}/>
                    <DeleteEspacioModal isOpen={showDeleteModal} onClose={()=>setShowDeleteModal(false)} espacio={selectedEspacio} onDeleted={loadEspacios}/>

                    {/* Preview imagen */}
                    {previewImg && (
                        <div onClick={()=>setPreviewImg(null)} style={{ position:'fixed', inset:0, zIndex:99999, background:'rgba(15,23,42,.75)', backdropFilter:'blur(8px)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'zoom-out' }}>
                            <button onClick={()=>setPreviewImg(null)} style={{ position:'absolute', top:'20px', right:'24px', width:'36px', height:'36px', borderRadius:'50%', background:'rgba(255,255,255,.15)', border:'none', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
                                <X size={18} color="#fff"/>
                            </button>
                            <img src={previewImg} alt="preview" style={{ maxWidth:'80vw', maxHeight:'80vh', borderRadius:'16px', boxShadow:'0 24px 80px rgba(0,0,0,.5)' }}/>
                        </div>
                    )}

                    {/* Contador */}
                    <div style={{ padding:'16px 24px', borderBottom:'1.5px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                            <div style={{ width:'32px', height:'32px', borderRadius:'10px', background:`${colors.primary}12`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                <Store size={15} color={colors.primary}/>
                            </div>
                            <span style={{ fontSize:'.82rem', fontWeight:'800', color:'#0f172a' }}>{espacios.length}</span>
                            <span style={{ fontSize:'.78rem', color:'#94a3b8', fontWeight:'500' }}>espacios registrados</span>
                        </div>
                        <span style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'600' }}>Página {currentPage} de {totalPages||1}</span>
                    </div>

                    <div className="custom-scroll" style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontFamily:"'DM Sans',sans-serif" }}>
                            <thead>
                                <tr style={{ background:'#fafcff', borderBottom:'1.5px solid #f1f5f9' }}>
                                    {['Sucursal','Nombre','Descripción','Capacidad','Imagen','Fecha creación','Estado','Acciones'].map((h,i)=>(
                                        <th key={i} style={{ padding:'12px 18px', textAlign:'left', fontSize:'.68rem', fontWeight:'800', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.08em', whiteSpace:'nowrap' }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {displayedEspacios.map(esp=>(
                                    <tr key={esp.id} className="sp-table-row" style={{ borderBottom:'1px solid #f8fafc' }}>
                                        <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                            <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:`${colors.secondary}10`, border:`1px solid ${colors.secondary}22`, color:colors.secondary, borderRadius:'6px', padding:'2px 8px', fontSize:'.72rem', fontWeight:'700' }}>
                                                {esp.sucursal_nombre||'N/A'}
                                            </span>
                                        </td>
                                        <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                            <span style={{ fontSize:'.85rem', fontWeight:'700', color:'#0f172a' }}>{esp.nombre}</span>
                                        </td>
                                        <td style={{ padding:'12px 18px', maxWidth:'200px' }}>
                                            <span style={{ fontSize:'.78rem', color:'#64748b', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>
                                                {esp.descripcion||'—'}
                                            </span>
                                        </td>
                                        <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                            <span style={{ display:'inline-flex', alignItems:'center', gap:'4px', fontSize:'.78rem', color:'#374151', fontWeight:'600' }}>
                                                <Users size={13} color={colors.accentDark}/> {esp.cantidad_personas}
                                            </span>
                                        </td>
                                        <td style={{ padding:'12px 18px' }}>
                                            {esp.imagen
                                                ? <img src={esp.imagen} alt={esp.nombre} className="sp-img" onClick={()=>setPreviewImg(esp.imagen!)}/>
                                                : <span style={{ fontSize:'.72rem', color:'#cbd5e1', fontStyle:'italic' }}>Sin imagen</span>
                                            }
                                        </td>
                                        <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                            <span style={{ fontSize:'.78rem', color:'#64748b', fontWeight:'500' }}>
                                                {new Date(esp.fecha_creacion).toLocaleDateString('es-ES',{day:'2-digit',month:'2-digit',year:'numeric'})}
                                            </span>
                                        </td>
                                        <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                            {esp.estado ? (
                                                <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:`${colors.secondaryLight}14`, border:`1px solid ${colors.secondaryLight}30`, color:'#065f46', borderRadius:'100px', padding:'3px 10px', fontSize:'.7rem', fontWeight:'700' }}>
                                                    <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:colors.secondaryLight, display:'inline-block' }}/>Activo
                                                </span>
                                            ) : (
                                                <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:`${colors.primary}10`, border:`1px solid ${colors.primary}22`, color:colors.primaryDark, borderRadius:'100px', padding:'3px 10px', fontSize:'.7rem', fontWeight:'700' }}>
                                                    <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:colors.primary, display:'inline-block' }}/>Inactivo
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                            <div style={{ display:'flex', gap:'6px' }}>
                                                {perm.can_update && (
                                                    <button className="sp-action-btn" onClick={()=>{ setSelectedEspacio(esp); setShowEditModal(true); }} title="Editar"
                                                        style={{ background:`${colors.secondary}12`, color:colors.secondary }}
                                                        onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background=colors.secondary; (e.currentTarget as HTMLButtonElement).style.color='#fff'; }}
                                                        onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background=`${colors.secondary}12`; (e.currentTarget as HTMLButtonElement).style.color=colors.secondary; }}>
                                                        <Edit size={14}/>
                                                    </button>
                                                )}
                                                {perm.can_delete && (
                                                    <button className="sp-action-btn" onClick={()=>{ setSelectedEspacio(esp); setShowDeleteModal(true); }} title="Eliminar"
                                                        style={{ background:`${colors.primary}10`, color:colors.primary }}
                                                        onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background=colors.primary; (e.currentTarget as HTMLButtonElement).style.color='#fff'; }}
                                                        onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background=`${colors.primary}10`; (e.currentTarget as HTMLButtonElement).style.color=colors.primary; }}>
                                                        <Trash2 size={14}/>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}

                                {espacios.length===0 && (
                                    <tr>
                                        <td colSpan={8} style={{ padding:'60px 24px', textAlign:'center' }}>
                                            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'12px' }}>
                                                <div style={{ width:'56px', height:'56px', borderRadius:'16px', background:`${colors.primary}10`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                    <Store size={24} color={colors.primary} style={{ opacity:.5 }}/>
                                                </div>
                                                <p style={{ fontSize:'1rem', fontWeight:'800', color:'#0f172a' }}>No hay espacios disponibles</p>
                                                {perm.can_create && <p style={{ fontSize:'.82rem', color:'#94a3b8' }}>Agrega un nuevo espacio para comenzar</p>}
                                                <div style={{ display:'flex', gap:'5px', marginTop:'4px' }}>
                                                    {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                                        <div key={i} style={{ height:'2px', width:i===0?'16px':'6px', borderRadius:'2px', background:c, opacity:.4 }}/>
                                                    ))}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {espacios.length>0 && (
                        <div style={{ borderTop:'1.5px solid #f1f5f9' }}>
                            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} itemsPerPage={itemsPerPage} totalItems={espacios.length}/>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default SpacePage;